import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import React, { useEffect, useMemo, useState } from "react";
import { MdAdd, MdOutlinePlaylistRemove } from "react-icons/md";

import { useClientManager } from "@megaron/react-clients";
import { TicketStatus, type TicketCategory } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";

import { SupportTagPickerContent } from "./SupportTagPickerContent";
import { isBlockedSupportTag, withoutBlockedSupportTags } from "./supportTagSuggestions";

type Props = {
  ticketId: Uuid;
  subject: string;
  status: TicketStatus;
  priority: "low" | "medium" | "high" | "urgent" | null;
  category: TicketCategory | null;
  invoiceNumber: string | null;
  orderNumber: string | null;
  ticketTags: string[];
  onSuccess: () => void;
  onClose: () => void;
};

export const SupportEditTagsDialog: React.FC<Props> = (props) => {
  const saveTags = useClientManager("support").updateTicket().useMutation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const ticketTags = useMemo(() => withoutBlockedSupportTags(props.ticketTags), [props.ticketTags]);

  const saveTicketTags = (tags: string[]) => {
    const normalizedTags = withoutBlockedSupportTags(tags)
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag, index, all) => tag.length > 0 && all.indexOf(tag) === index);
    saveTags.mutate(
      {
        id: props.ticketId,
        subject: props.subject,
        status: props.status,
        priority: props.priority,
        category: props.category,
        tags: normalizedTags,
        invoiceNumber: props.invoiceNumber,
        orderNumber: props.orderNumber,
        reporterName: undefined,
        reporterEmail: undefined,
        reporterPhone: undefined,
      },
      {
        onSuccess: () => {
          props.onSuccess();
        },
      },
    );
  };

  useEffect(() => {
    if (ticketTags.length === props.ticketTags.length) return;
    saveTicketTags(ticketTags);
  }, [props.ticketTags, ticketTags]);

  const handleAddTag = () => {
    const trimmed = newTag.trim().toLowerCase();
    if (!trimmed || isBlockedSupportTag(trimmed) || ticketTags.includes(trimmed)) return;
    saveTicketTags([...ticketTags, trimmed]);
    setNewTag("");
    setIsAddDialogOpen(false);
  };

  return (
    <>
      <Dialog
        header={
          <div
            css={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "white",
              fontSize: "1rem",
            }}
          >
            <span>
              Wybrano: <strong>{ticketTags.length}</strong>
            </span>
            <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <MdAdd css={{ height: "20px", width: "20px", cursor: "pointer" }} onClick={() => setIsAddDialogOpen(true)} />
              <MdOutlinePlaylistRemove
                css={{ height: "20px", width: "20px", cursor: "pointer" }}
                onClick={() => saveTicketTags([])}
              />
            </div>
          </div>
        }
        placement="center"
        zIndex={1010}
        checkCloseButton
        onClose={props.onClose}
        contentCss={{
          width: "min(520px, calc(100vw - 2rem))",
          height: "min(42rem, calc(100dvh - 4.5rem))",
          maxHeight: "min(42rem, calc(100dvh - 4.5rem))",
          margin: "0.75rem",
        }}
      >
        <SupportTagPickerContent selectedTags={ticketTags} onSelectedTagsChange={saveTicketTags} />
      </Dialog>

      {isAddDialogOpen ? (
        <Dialog header="Dodaj nowy tag" zIndex={1011} onClose={() => setIsAddDialogOpen(false)}>
          <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <TextField value={newTag} onChange={setNewTag} autoFocus />
            <Button onClick={handleAddTag} isDisabled={!newTag.trim() || isBlockedSupportTag(newTag)}>
              Zapisz
            </Button>
          </div>
        </Dialog>
      ) : null}
    </>
  );
};
